import { useCallback, useEffect, useMemo, useState } from "react";

const getStorageKey = (userKey, suffix) => `sidebar_${suffix}_${userKey}`;

const readJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const useSidebarPreferences = (userKey, sections) => {
  const pinsKey = getStorageKey(userKey, "pins");
  const collapsedKey = getStorageKey(userKey, "collapsed");

  const defaultCollapsed = useMemo(() => {
    const map = {};
    sections.forEach((section) => {
      if (section.collapsible) {
        map[section.id] = section.defaultOpen === false;
      }
    });
    return map;
  }, [sections]);

  const [pinnedIds, setPinnedIds] = useState(() =>
    readJson(pinsKey, [])
  );
  const [collapsedSections, setCollapsedSections] = useState(() => {
    const stored = readJson(collapsedKey, null);
    return stored || defaultCollapsed;
  });

  useEffect(() => {
    setPinnedIds(readJson(pinsKey, []));
    const stored = readJson(collapsedKey, null);
    setCollapsedSections(stored || defaultCollapsed);
  }, [userKey, pinsKey, collapsedKey, defaultCollapsed]);

  const togglePin = useCallback(
    (itemId) => {
      setPinnedIds((prev) => {
        const next = prev.includes(itemId)
          ? prev.filter((id) => id !== itemId)
          : [...prev, itemId];
        writeJson(pinsKey, next);
        return next;
      });
    },
    [pinsKey]
  );

  const toggleSection = useCallback(
    (sectionId) => {
      setCollapsedSections((prev) => {
        const next = { ...prev, [sectionId]: !prev[sectionId] };
        writeJson(collapsedKey, next);
        return next;
      });
    },
    [collapsedKey]
  );

  const isPinned = useCallback(
    (itemId) => pinnedIds.includes(itemId),
    [pinnedIds]
  );

  const isSectionOpen = useCallback(
    (sectionId, defaultOpen = false) => {
      if (collapsedSections[sectionId] === undefined) {
        return defaultOpen !== false;
      }
      return !collapsedSections[sectionId];
    },
    [collapsedSections]
  );

  const expandSection = useCallback(
    (sectionId) => {
      setCollapsedSections((prev) => {
        if (!prev[sectionId]) return prev;
        const next = { ...prev, [sectionId]: false };
        writeJson(collapsedKey, next);
        return next;
      });
    },
    [collapsedKey]
  );

  return {
    pinnedIds,
    togglePin,
    toggleSection,
    expandSection,
    isPinned,
    isSectionOpen,
  };
};
